import React, { useState, useEffect } from 'react';
import { 
  Card, Table, Button, Space, message, Modal, Form, 
  Input, Select, Popconfirm, Tag, Typography, Tooltip
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { getFillBlanks, addFillBlank, updateFillBlank, deleteFillBlank } from '../../api/admin';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const FillBlankManage = () => {
  const [fillblanks, setFillblanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingFillBlank, setEditingFillBlank] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadFillBlanks();
  }, []);

  const loadFillBlanks = async () => {
    try {
      setLoading(true);
      const response = await getFillBlanks();
      if (response.code === 200) {
        // 解析 hints JSON
        const data = response.data.fillblanks.map(item => ({
          ...item,
          hintsArray: item.hints ? JSON.parse(item.hints) : []
        }));
        setFillblanks(data);
      }
    } catch (error) {
      message.error('加载完形填空失败');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingFillBlank(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingFillBlank(record);
    form.setFieldsValue({
      ...record,
      hints: record.hintsArray ? record.hintsArray.join('\n') : ''
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await deleteFillBlank(id);
      if (response.code === 200) {
        message.success('删除成功');
        loadFillBlanks();
      }
    } catch (error) {
      message.error(error.message || '删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // 处理 hints: 按行分割成数组
      const hintsArray = values.hints 
        ? values.hints.split('\n').filter(h => h.trim())
        : [];
      
      const submitData = {
        item_number: values.item_number,
        content_text: values.content_text,
        correct_answer: values.correct_answer,
        hints: JSON.stringify(hintsArray)
      };
      
      if (editingFillBlank) {
        const response = await updateFillBlank(editingFillBlank.id, submitData);
        if (response.code === 200) {
          message.success('更新成功');
        }
      } else {
        const response = await addFillBlank(submitData);
        if (response.code === 200) {
          message.success('添加成功');
        }
      }
      
      setModalVisible(false);
      loadFillBlanks();
    } catch (error) {
      message.error(error.message || '操作失败');
    }
  };

  const columns = [
    {
      title: '准则序号',
      dataIndex: 'item_number',
      key: 'item_number',
      width: 100,
      render: (num) => <Tag color="blue">准则{num}</Tag>
    },
    {
      title: '题目内容',
      dataIndex: 'content_text',
      key: 'content_text',
      ellipsis: true,
      render: (text) => {
        // 将 ___ 高亮显示
        const parts = text.split('___');
        return (
          <Text>
            {parts.map((part, index) => (
              <span key={index}>
                {part}
                {index < parts.length - 1 && (
                  <Tag color="orange" style={{ margin: '0 2px' }}>填空</Tag>
                )}
              </span>
            ))}
          </Text>
        );
      }
    },
    {
      title: '正确答案',
      dataIndex: 'correct_answer',
      key: 'correct_answer',
      width: 120,
      render: (text) => <Tag color="green">{text}</Tag>
    },
    {
      title: '提示数量',
      dataIndex: 'hintsArray',
      key: 'hints_count',
      width: 100,
      render: (arr) => arr ? arr.length : 0
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button 
              type="link" 
              size="small"
              icon={<EditOutlined />} 
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="确定删除此题目?"
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="删除">
              <Button 
                type="link" 
                size="small"
                danger 
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card 
        title="完形填空题库管理"
        extra={
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAdd}
          >
            添加题目
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={fillblanks}
          rowKey="id"
          loading={loading}
          pagination={false}
        />
      </Card>

      <Modal
        title={editingFillBlank ? '编辑完形填空' : '添加完形填空'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="item_number"
            label="所属准则"
            rules={[{ required: true, message: '请选择准则' }]}
          >
            <Select>
              <Option value={1}>准则1: 先执行再优化</Option>
              <Option value={2}>准则2: 守时守信守规矩</Option>
              <Option value={3}>准则3: 沟通坦诚讲真话</Option>
              <Option value={4}>准则4: 主动担当不甩锅</Option>
              <Option value={5}>准则5: 以身作则带队伍</Option>
              <Option value={6}>准则6: 目标导向重实绩</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="content_text"
            label="题目内容"
            rules={[{ required: true, message: '请输入题目内容' }]}
            extra="请使用 ___ (三个下划线) 表示填空位置"
          >
            <TextArea rows={3} placeholder="例如: 先落地、后完善,不找___、不先谈困难。" />
          </Form.Item>

          <Form.Item
            name="correct_answer"
            label="正确答案"
            rules={[{ required: true, message: '请输入正确答案' }]}
          >
            <Input placeholder="填写正确答案" />
          </Form.Item>

          <Form.Item
            name="hints"
            label="提示内容"
            extra="每行一个提示,例如: 提示1: xxx"
          >
            <TextArea rows={3} placeholder="提示1: 找理由推脱&#10;提示2: 两个字" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FillBlankManage;
